
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { talents } from '../../mocks/talents';
import { clients } from '../../mocks/clients';
import { projects } from '../../mocks/projects';

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [isAdvancedSearch, setIsAdvancedSearch] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResults, setAiResults] = useState<string>('');
  
  // 高级搜索条件
  const [advancedFilters, setAdvancedFilters] = useState({
    experience: '',
    education: '',
    location: '',
    salary: '',
    skills: '',
    status: '',
    industry: '',
    company: ''
  });

  const handleSearch = () => {
    if (!searchQuery.trim() && !isAdvancedSearch) return;
    
    let results: any[] = [];
    
    if (searchType === 'all' || searchType === 'talents') {
      const talentResults = talents.filter(talent => {
        const basicMatch = talent.name.includes(searchQuery) ||
          talent.position.includes(searchQuery) ||
          talent.skills.some(skill => skill.includes(searchQuery)) ||
          talent.location.includes(searchQuery);
        
        const advancedMatch = !isAdvancedSearch || (
          (!advancedFilters.experience || talent.experience.includes(advancedFilters.experience)) &&
          (!advancedFilters.education || talent.education.includes(advancedFilters.education)) &&
          (!advancedFilters.location || talent.location.includes(advancedFilters.location)) &&
          (!advancedFilters.skills || talent.skills.some(skill => skill.includes(advancedFilters.skills))) &&
          (!advancedFilters.status || talent.status.includes(advancedFilters.status))
        );
        
        return basicMatch && advancedMatch;
      }).map(talent => ({ ...talent, type: 'talent' }));
      results = [...results, ...talentResults];
    }
    
    if (searchType === 'all' || searchType === 'projects') {
      const projectResults = projects.filter(project => {
        const basicMatch = project.title.includes(searchQuery) ||
          project.clientName.includes(searchQuery) ||
          project.location.includes(searchQuery);
        
        const advancedMatch = !isAdvancedSearch || (
          (!advancedFilters.location || project.location.includes(advancedFilters.location)) &&
          (!advancedFilters.salary || project.salary.includes(advancedFilters.salary)) &&
          (!advancedFilters.status || project.status.includes(advancedFilters.status))
        );
        
        return basicMatch && advancedMatch;
      }).map(project => ({ ...project, type: 'project' }));
      results = [...results, ...projectResults];
    }
    
    if (searchType === 'all' || searchType === 'clients') {
      const clientResults = clients.filter(client => {
        const basicMatch = client.companyName.includes(searchQuery) ||
          client.industry.includes(searchQuery) ||
          client.location.includes(searchQuery);
        
        const advancedMatch = !isAdvancedSearch || (
          (!advancedFilters.industry || client.industry.includes(advancedFilters.industry)) &&
          (!advancedFilters.location || client.location.includes(advancedFilters.location)) &&
          (!advancedFilters.company || client.companyName.includes(advancedFilters.company))
        );
        
        return basicMatch && advancedMatch;
      }).map(client => ({ ...client, type: 'client' }));
      results = [...results, ...clientResults];
    }
    
    setSearchResults(results);
  };

  const handleAIQuery = () => {
    if (!aiQuery.trim()) return;
    
    // 模拟AI回答
    let response = '';
    if (aiQuery.includes('顾问') && aiQuery.includes('项目')) {
      response = '根据数据分析，张经理本月完成了3个项目，李专员完成了2个项目，王总监完成了4个项目。王总监完成项目最多。';
    } else if (aiQuery.includes('人才') && aiQuery.includes('技能')) {
      response = '系统中共有156位人才，其中React技能的有45人，Vue技能的有38人，Java技能的有52人，Python技能的有41人。';
    } else if (aiQuery.includes('客户') && aiQuery.includes('行业')) {
      response = '客户行业分布：互联网行业占40%，金融行业占25%，制造业占20%，教育行业占15%。互联网行业客户最多。';
    } else {
      response = '抱歉，我需要更具体的问题才能为您提供准确的分析。您可以询问关于人才技能分布、项目完成情况、客户行业分析等问题。';
    }
    
    setAiResults(response);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '在职-考虑机会': return 'bg-yellow-100 text-yellow-800';
      case '离职-立即到岗': return 'bg-green-100 text-green-800';
      case '招聘中': return 'bg-blue-100 text-blue-800';
      case '面试中': return 'bg-orange-100 text-orange-800';
      case '已完成': return 'bg-green-100 text-green-800';
      case '暂停': return 'bg-gray-100 text-gray-800';
      case '长期合作': return 'bg-green-100 text-green-800';
      case '活跃合作': return 'bg-blue-100 text-blue-800';
      case '新客户': return 'bg-yellow-100 text-yellow-800';
      case '潜在客户': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderSearchResult = (result: any) => {
    if (result.type === 'talent') {
      return (
        <Card key={`talent-${result.id}`} className="mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <i className="ri-user-line text-blue-600 text-xl"></i>
                <h3 className="text-lg font-semibold text-gray-900">{result.name}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(result.status)}`}>
                  {result.status}
                </span>
              </div>
              <p className="text-gray-600 mb-2">{result.position} · {result.experience} · {result.education}</p>
              <p className="text-sm text-gray-500 mb-2">{result.school} · {result.major}</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {result.skills.slice(0, 5).map((skill: string, index: number) => (
                  <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                    {skill}
                  </span>
                ))}
                {result.skills.length > 5 && (
                  <span className="bg-gray-50 text-gray-600 px-2 py-1 rounded text-xs">
                    +{result.skills.length - 5}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{result.location} · {result.expectedSalary}</p>
            </div>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline">查看详情</Button>
              <Button size="sm">联系</Button>
            </div>
          </div>
        </Card>
      );
    }
    
    if (result.type === 'project') {
      return (
        <Card key={`project-${result.id}`} className="mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <i className="ri-briefcase-line text-green-600 text-xl"></i>
                <h3 className="text-lg font-semibold text-gray-900">{result.title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(result.status)}`}>
                  {result.status}
                </span>
              </div>
              <p className="text-gray-600 mb-2">{result.clientName} · {result.salary}</p>
              <p className="text-sm text-gray-500 mb-2">{result.location} · {result.experienceRequired} · {result.education}</p>
              <p className="text-sm text-gray-500">招聘 {result.headcount} 人 · 已有 {result.currentApplications} 人申请</p>
            </div>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline">查看详情</Button>
              <Button size="sm">管理</Button>
            </div>
          </div>
        </Card>
      );
    }
    
    if (result.type === 'client') {
      return (
        <Card key={`client-${result.id}`} className="mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <i className="ri-building-line text-purple-600 text-xl"></i>
                <h3 className="text-lg font-semibold text-gray-900">{result.companyName}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(result.cooperationStatus)}`}>
                  {result.cooperationStatus}
                </span>
              </div>
              <p className="text-gray-600 mb-2">{result.industry} · {result.scale}人 · {result.location}</p>
              <p className="text-sm text-gray-500 mb-2">联系人: {result.contactPerson} · {result.phone}</p>
              <p className="text-sm text-gray-500">合作项目: {result.projectCount} 个</p>
            </div>
            <div className="flex space-x-2">
              <Button size="sm" variant="outline">查看详情</Button>
              <Button size="sm">联系</Button>
            </div>
          </div>
        </Card>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">智能搜索</h1>
          <p className="text-gray-600">快速搜索人才、项目和客户信息，支持AI自然语言问答</p>
        </div>

        {/* AI自然语言问答 */}
        <Card className="mb-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <i className="ri-robot-line text-blue-600 text-xl"></i>
              <h3 className="text-lg font-semibold text-gray-900">AI智能问答</h3>
            </div>
            <div className="flex space-x-4">
              <div className="flex-1">
                <Input
                  placeholder="输入自然语言问题，如：最近哪个顾问完成项目最多？"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                />
              </div>
              <Button onClick={handleAIQuery}>
                <i className="ri-send-plane-line mr-2"></i>
                提问
              </Button>
            </div>
            
            {/* 常见问题示例 */}
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">常见问题示例：</div>
              <div className="flex flex-wrap gap-2">
                {[
                  '最近哪个顾问完成项目最多？',
                  '系统中有多少React技能的人才？',
                  '哪个行业的客户最多？',
                  '本月新增了多少人才？',
                  '平均招聘周期是多少天？'
                ].map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setAiQuery(question)}
                    className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm cursor-pointer hover:bg-blue-100"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            {/* AI回答结果 */}
            {aiResults && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <i className="ri-robot-line text-blue-600 text-xl mt-0.5"></i>
                  <div>
                    <div className="font-medium text-blue-800 mb-2">AI分析结果：</div>
                    <p className="text-blue-700">{aiResults}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* 传统搜索 */}
        <Card className="mb-8">
          <div className="space-y-6">
            {/* 搜索类型选择 */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">搜索范围:</span>
              <div className="flex space-x-2">
                {[
                  { value: 'all', label: '全部' },
                  { value: 'talents', label: '人才' },
                  { value: 'projects', label: '项目' },
                  { value: 'clients', label: '客户' }
                ].map(type => (
                  <button
                    key={type.value}
                    onClick={() => setSearchType(type.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${
                      searchType === type.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 基础搜索 */}
            <div className="flex space-x-4">
              <div className="flex-1">
                <Input
                  placeholder="输入关键词搜索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button onClick={handleSearch}>
                <i className="ri-search-line mr-2"></i>
                搜索
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsAdvancedSearch(!isAdvancedSearch)}
              >
                <i className="ri-filter-line mr-2"></i>
                高级搜索
              </Button>
            </div>

            {/* 高级搜索 */}
            {isAdvancedSearch && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">高级搜索条件</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    label="工作经验"
                    placeholder="如: 3-5年"
                    value={advancedFilters.experience}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, experience: e.target.value }))}
                  />
                  <Input
                    label="学历要求"
                    placeholder="如: 本科"
                    value={advancedFilters.education}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, education: e.target.value }))}
                  />
                  <Input
                    label="工作地点"
                    placeholder="如: 北京"
                    value={advancedFilters.location}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, location: e.target.value }))}
                  />
                  <Input
                    label="薪资范围"
                    placeholder="如: 20K-30K"
                    value={advancedFilters.salary}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, salary: e.target.value }))}
                  />
                  <Input
                    label="技能关键词"
                    placeholder="如: React, Vue"
                    value={advancedFilters.skills}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, skills: e.target.value }))}
                  />
                  <Input
                    label="状态"
                    placeholder="如: 在职-考虑机会"
                    value={advancedFilters.status}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, status: e.target.value }))}
                  />
                  <Input
                    label="行业"
                    placeholder="如: 互联网"
                    value={advancedFilters.industry}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, industry: e.target.value }))}
                  />
                  <Input
                    label="公司名称"
                    placeholder="如: 阿里巴巴"
                    value={advancedFilters.company}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, company: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end space-x-4 mt-4">
                  <Button variant="outline" onClick={() => setAdvancedFilters({
                    experience: '', education: '', location: '', salary: '', skills: '', status: '', industry: '', company: ''
                  })}>
                    清空条件
                  </Button>
                  <Button onClick={handleSearch}>应用筛选</Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* 搜索结果 */}
        {searchResults.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                搜索结果 ({searchResults.length})
              </h2>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">排序方式:</span>
                <select className="border border-gray-300 rounded-md px-3 py-1 text-sm pr-8">
                  <option>相关度</option>
                  <option>更新时间</option>
                  <option>薪资</option>
                  <option>匹配度</option>
                </select>
              </div>
            </div>
            
            {/* 结果聚合摘要 */}
            <Card className="mb-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">搜索结果摘要</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {searchResults.filter(r => r.type === 'talent').length}
                    </div>
                    <div className="text-sm text-blue-700">匹配人才</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {searchResults.filter(r => r.type === 'project').length}
                    </div>
                    <div className="text-sm text-green-700">相关项目</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {searchResults.filter(r => r.type === 'client').length}
                    </div>
                    <div className="text-sm text-purple-700">相关客户</div>
                  </div>
                </div>
              </div>
            </Card>
            
            <div>
              {searchResults.map(result => renderSearchResult(result))}
            </div>
          </div>
        )}

        {searchResults.length === 0 && searchQuery && (
          <Card>
            <div className="text-center py-12">
              <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">未找到相关结果</h3>
              <p className="text-gray-500">请尝试其他关键词或调整搜索条件</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
